import React, { useState } from "react";
import { toast } from "sonner";
import { Database, ArrowRight, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const AYLAR = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];

export default function LegacyMigrationPanel() {
  const now = new Date();
  const [scope, setScope] = useState("all"); // "all" | "month"
  const [yil, setYil] = useState(now.getFullYear());
  const [ay, setAy] = useState(now.getMonth() + 1);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const buildPayload = () => {
    const p = { scope, dry_run: true };
    if (scope === "month") { p.yil = Number(yil); p.ay = Number(ay); }
    return p;
  };

  const runPreview = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/annual-leave/legacy-migrate", buildPayload());
      setPreview(r.data);
      toast.success(`${r.data.candidate_count} aday satır bulundu`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Önizleme başarısız");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview || preview.candidate_count === 0) return;
    if (!window.confirm(`${preview.candidate_count} payroll_entries kaydı taşınacak. Devam?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/annual-leave/legacy-migrate", { ...buildPayload(), dry_run: false });
      setResult(r.data);
      toast.success(`${r.data.created} kayıt başarıyla taşındı`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Taşıma başarısız");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="legacy-migration-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 grid place-items-center shrink-0">
          <Database size={18} />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-extrabold text-slate-900 text-base">Legacy Puantaj Taşıma</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Eski manuel <code className="bg-slate-100 px-1 rounded">ucretli_izin_gun</code> değerlerini,
            Yıllık İzinler koleksiyonuna tarih aralığı olarak taşır. Puantaj alanı sıfırlanır — toplam gün sayısı değişmez.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <div className="md:col-span-3">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">Kapsam</label>
          <div className="flex flex-wrap gap-2">
            <label className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 cursor-pointer text-xs">
              <input type="radio" name="migscope" value="all" checked={scope==="all"} onChange={() => setScope("all")} data-testid="mig-scope-all"/>
              <strong>Tüm veri</strong>
            </label>
            <label className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 cursor-pointer text-xs">
              <input type="radio" name="migscope" value="month" checked={scope==="month"} onChange={() => setScope("month")} data-testid="mig-scope-month"/>
              <strong>Seçili ay</strong>
            </label>
          </div>
        </div>
        {scope === "month" && (
          <>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">Yıl</label>
              <input type="number" value={yil} min={2020} max={2040} onChange={(e) => setYil(e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="mig-yil"/>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">Ay</label>
              <select value={ay} onChange={(e) => setAy(e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="mig-ay">
                {AYLAR.map((n, i) => <option key={i} value={i+1}>{n}</option>)}
              </select>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={runPreview}
          disabled={busy}
          data-testid="mig-preview-btn"
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50"
        >
          {busy ? <Loader2 size={13} className="animate-spin"/> : <ArrowRight size={13}/>}
          Önizle
        </button>
        {preview && preview.candidate_count > 0 && !result && (
          <button
            onClick={apply}
            disabled={busy}
            data-testid="mig-apply-btn"
            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-md disabled:opacity-50"
          >
            {busy ? <Loader2 size={13} className="animate-spin"/> : <CheckCircle2 size={13}/>}
            {preview.candidate_count} Kaydı Taşı
          </button>
        )}
        {result && (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1">
            <CheckCircle2 size={12}/> {result.created} kayıt taşındı
          </span>
        )}
      </div>

      {preview && (
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <div className="bg-slate-100 px-3 py-1.5 text-xs font-bold flex items-center gap-2">
            {preview.candidate_count === 0 ? (
              <>
                <AlertTriangle size={12} className="text-amber-600"/>
                Taşınacak kayıt bulunamadı (hepsi önceden taşınmış ya da ücretli izin = 0).
              </>
            ) : (
              <>Önizleme: {preview.candidate_count} aday satır</>
            )}
          </div>
          {preview.candidate_count > 0 && (
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left">
                    <th className="px-2 py-1 font-bold">Personel</th>
                    <th className="px-2 py-1 font-bold">Dönem</th>
                    <th className="px-2 py-1 font-bold text-right">Manuel</th>
                    <th className="px-2 py-1 font-bold">Yeni Tarih Aralığı</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {preview.rows.map((r, i) => (
                    <tr key={i} data-testid={`mig-row-${i}`}>
                      <td className="px-2 py-1">
                        <div className="font-semibold">{r.ad_soyad}</div>
                        <div className="text-[10px] text-slate-500">{r.sicil_no}</div>
                      </td>
                      <td className="px-2 py-1 font-mono">{r.yil}-{String(r.ay).padStart(2,"0")}</td>
                      <td className="px-2 py-1 text-right font-mono text-amber-700">{r.manuel_gun}g</td>
                      <td className="px-2 py-1 font-mono text-emerald-700">{r.yeni_baslangic} → {r.yeni_bitis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
